import { useState, useRef, useEffect } from "react";
import styles from "../HealthChat.module.css";
import { MessageIcon, MicIcon, SendIcon } from "../icons";
import VoiceScreen from "./VoiceScreen";
import useVoiceChat from "../../../hooks/useVoiceChat";

async function translateToEnglish(text, sourceLang) {
  if (!text || sourceLang === "en") return text;
  try {
    const res = await fetch(
      `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${sourceLang}&tl=en&dt=t&q=${encodeURIComponent(text)}`,
    );
    const data = await res.json();
    return data[0]?.map((item) => item[0]).join("") || text;
  } catch {
    return text;
  }
}

async function translateFromEnglish(text, targetLang) {
  if (!text || targetLang === "en") return text;
  try {
    const res = await fetch(
      `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=${targetLang}&dt=t&q=${encodeURIComponent(text)}`,
    );
    const data = await res.json();
    return data[0]?.map((item) => item[0]).join("") || text;
  } catch {
    return text;
  }
}

export default function ChatScreen({ t, tVoice, onSummary, lang, onBack }) {
  const [tab, setTab] = useState("chat");
  const [messages, setMessages] = useState([]);
  const [textInput, setTextInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  const RASA_URL =
    process.env.NEXT_PUBLIC_RASA_URL ||
    "https://femcare.avexa.net/chatbot/webhooks/rest/webhook";

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const {
    isListening,
    liveText,
    isSpeaking,
    micError,
    toggleMic,
    speak,
    speakAll,
    stopSpeaking,
    isSpeechSupported,
  } = useVoiceChat({
    lang,
    autoSend: true,
    silenceMs: 1800,
    onSendText: (capturedText) => sendMessage(capturedText),
  });

  const hasText = textInput.trim().length > 0;

  async function sendMessage(userText) {
    if (!userText?.trim() || loading) return;

    stopSpeaking();

    let displayUserText = userText;
    if (lang !== "en") {
      try {
        displayUserText = await translateFromEnglish(
          await translateToEnglish(userText, lang),
          lang,
        );
      } catch {
        displayUserText = userText;
      }
    }

    const userMsg = {
      id: Date.now(),
      from: "user",
      text: displayUserText,
      raw: userText,
    };

    setMessages((prev) => [...prev, userMsg]);
    setTextInput("");
    setLoading(true);

    try {
      const englishText = await translateToEnglish(userText, lang);

      const res = await fetch(RASA_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sender: `avexa-web-user-${lang}`,
          message: englishText,
          metadata: { originalLanguage: lang },
        }),
      });

      const data = await res.json();
      const rasaMsgs = [];

      for (const msg of data) {
        let translatedText = msg.text;
        if (msg.text && lang !== "en") {
          translatedText = await translateFromEnglish(msg.text, lang);
        }

        let translatedButtons = msg.buttons || [];
        if (msg.buttons && lang !== "en") {
          translatedButtons = await Promise.all(
            msg.buttons.map(async (b) => ({
              ...b,
              title: await translateFromEnglish(b.title, lang),
            })),
          );
        }

        rasaMsgs.push({
          id: Date.now() + Math.random(),
          from: "bot",
          text: translatedText,
          buttons: translatedButtons,
          custom: msg.custom || null,
        });
      }

      setMessages((prev) => [...prev, ...rasaMsgs]);

      speakAll(rasaMsgs);

      const customMsg = data.find((m) => m.custom?.type === "summary");
      if (customMsg) onSummary(customMsg.custom);
    } catch {
      const errText = t.errorMsg || "Something went wrong. Please try again.";
      setMessages((prev) => [
        ...prev,
        { id: Date.now(), from: "bot", text: errText },
      ]);
      speak(errText);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className={styles.padding}>
        <button className={styles.selectDoctorBack} onClick={onBack}>
          ← Back
        </button>
      </div>

      {tab === "chat" && (
        <div className={styles.body}>
          <div className={styles.botBubble}>
            {t.welcome} <b>{t.welcomeBold}</b>.
            <br />
            {t.welcomeEnd}
          </div>

          {messages.map((msg, i) => (
            <div key={msg.id || i}>
              {msg.from === "bot" ? (
                <>
                  <div style={{ display: "flex", gap: 6 }}>
                    <div className={styles.botMsg} style={{ flex: 1 }}>
                      {msg.text}
                    </div>
                    {msg.text && (
                      <button onClick={() => speak(msg.text)}>🔊</button>
                    )}
                  </div>

                  {msg.buttons?.length > 0 && (
                    <div className={styles.btnGroup}>
                      {msg.buttons.map((b) => (
                        <button
                          key={b.title}
                          className={styles.optionBtn}
                          onClick={() => {
                            const clean = (b.payload || b.title)
                              .replace(/^\//, "")
                              .replace(/_/g, " ");
                            speak(b.title);
                            sendMessage(clean);
                          }}
                        >
                          {b.title}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className={styles.userMsg}>{msg.text}</div>
              )}
            </div>
          ))}

          {loading && (
            <div className={styles.typingDot}>
              <span />
              <span />
              <span />
            </div>
          )}

          {isSpeaking && (
            <div>
              🔊 Speaking...
              <button onClick={stopSpeaking}>✕</button>
            </div>
          )}

          <div ref={bottomRef} />
        </div>
      )}

      {tab === "voice" && <VoiceScreen t={tVoice} lang={lang} />}

      {tab === "chat" && (
        <>
          <div className={styles.inputWrap}>
            <input
              className={styles.input}
              placeholder={isListening ? "Listening…" : t.placeholder}
              value={isListening ? liveText : textInput}
              onChange={(e) => !isListening && setTextInput(e.target.value)}
              onKeyDown={(e) =>
                e.key === "Enter" && !isListening && sendMessage(textInput)
              }
              readOnly={isListening}
            />

            {/*  Smart Toggle Button */}
            {!hasText && !isListening ? (
              <button
                className={styles.micIconBtn}
                onClick={toggleMic}
                disabled={!isSpeechSupported}
              >
                <MicIcon size={18} />
              </button>
            ) : (
              <button
                className={styles.sendBtn}
                onClick={() =>
                  isListening ? toggleMic() : sendMessage(textInput)
                }
              >
                <SendIcon size={15} color="#fff" />
              </button>
            )}
          </div>

          {isListening && <div>🎤 Listening...</div>}
          {micError && <div style={{ color: "red" }}>{micError}</div>}
        </>
      )}

    </>
  );
}

